import { ChangeDetectionStrategy, Component, ElementRef, HostListener, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RippleModule } from 'primeng/ripple';
import { TranslateModule } from '@ngx-translate/core';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword, User } from 'firebase/auth';
import { getFirebaseAuth } from '../../firebase-desktop';
import { BackofficeSessionService } from '../../backoffice-session.service';
import { initialsFrom, passwordErrorKey } from './app.profile-menu.util';

type PasswordFeedback = { kind: 'success' | 'error'; messageKey: string } | null;

/**
 * The signed-in admin's corner of the topbar: who I am, which email I am on,
 * and a way to change my password without leaving the back office. Everything
 * comes from Firebase Auth, which is what the back office signs in with.
 */
@Component({
  selector: 'bo-profile-menu',
  templateUrl: './app.profile-menu.component.html',
  styleUrls: ['./app.profile-menu.component.scss'],
  imports: [CommonModule, FormsModule, RippleModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppProfileMenuComponent implements OnInit {
  #host = inject(ElementRef<HTMLElement>);
  #session = inject(BackofficeSessionService);

  readonly open = signal(false);
  readonly email = signal<string | null>(null);
  readonly displayName = signal<string | null>(null);
  readonly photoUrl = signal<string | null>(null);
  /** Only an email/password account can change its password here. */
  readonly canChangePassword = signal(false);

  readonly showPasswordForm = signal(false);
  readonly currentPassword = signal('');
  readonly newPassword = signal('');
  readonly confirmPassword = signal('');
  readonly saving = signal(false);
  readonly feedback = signal<PasswordFeedback>(null);

  ngOnInit(): void {
    const auth = getFirebaseAuth();
    this.applyUser(auth.currentUser);
    // currentUser can still be resolving on a fresh load.
    auth.onAuthStateChanged((user) => this.applyUser(user));
  }

  private applyUser(user: User | null): void {
    this.email.set(user?.email ?? null);
    this.displayName.set(user?.displayName ?? null);
    this.photoUrl.set(user?.photoURL ?? null);
    this.canChangePassword.set((user?.providerData ?? []).some((provider) => provider.providerId === 'password'));
  }

  /** First and, when there is one, last initial — the avatar when there is no photo. */
  initials(): string {
    return initialsFrom(this.displayName(), this.email());
  }

  toggle(): void {
    this.open.update((value) => !value);
    if (!this.open()) {
      this.resetPasswordForm();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.open() && !this.#host.nativeElement.contains(event.target)) {
      this.open.set(false);
      this.resetPasswordForm();
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open()) {
      this.open.set(false);
      this.resetPasswordForm();
    }
  }

  openPasswordForm(): void {
    this.showPasswordForm.set(true);
    this.feedback.set(null);
  }

  private resetPasswordForm(): void {
    this.showPasswordForm.set(false);
    this.currentPassword.set('');
    this.newPassword.set('');
    this.confirmPassword.set('');
    this.feedback.set(null);
    this.saving.set(false);
  }

  get passwordValid(): boolean {
    return (
      this.currentPassword().length > 0 &&
      this.newPassword().length >= 6 &&
      this.newPassword() === this.confirmPassword()
    );
  }

  async submitPassword(): Promise<void> {
    if (!this.passwordValid || this.saving()) {
      return;
    }
    this.saving.set(true);
    this.feedback.set(null);

    const auth = getFirebaseAuth();
    const user = auth.currentUser;
    if (!user?.email) {
      this.saving.set(false);
      this.feedback.set({ kind: 'error', messageKey: 'profileMenu.pwdErrorGeneric' });
      return;
    }

    try {
      // Re-authenticate with the current password: Firebase requires a recent
      // login to change it, and this proves the person at the keyboard knows it.
      const credential = EmailAuthProvider.credential(user.email, this.currentPassword());
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, this.newPassword());
      this.currentPassword.set('');
      this.newPassword.set('');
      this.confirmPassword.set('');
      this.showPasswordForm.set(false);
      this.feedback.set({ kind: 'success', messageKey: 'profileMenu.pwdSuccess' });
    } catch (error: unknown) {
      const code = (error as { code?: string })?.code;
      this.feedback.set({ kind: 'error', messageKey: passwordErrorKey(code) });
    } finally {
      this.saving.set(false);
    }
  }

  logout(): void {
    this.open.set(false);
    this.#session.endSession({ navigate: true });
  }
}
